import { _isNotEmpty } from '@/littledash';
import ActionList, { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import SelectDropDown from '@/components/UI/SelectDropDown';
import Link from '@/components/UI/Link';
import Button from '@/components/UI/Button';
import { type FC } from 'react';
import type { ID } from '@/model/Common.model';

interface AnimalsTableNoDataProps {
  atunesIntegrationEnabled: boolean;
  studyId: ID;
  actions: Array<ActionProps>;
  onCreateNewAnimals: () => void;
  animalsExistInStudy?: boolean;
}

/**
 * Empty-state component for the animals table. Distinguishes between a study
 * that genuinely has no animals (offers create/import affordances) and one
 * where all rows are hidden by the active filters or search query.
 */
const AnimalsTableNoData: FC<AnimalsTableNoDataProps> = ({
  atunesIntegrationEnabled,
  studyId,
  actions,
  onCreateNewAnimals,
  animalsExistInStudy,
}) => {
  if (animalsExistInStudy === true) {
    return (
      <div
        className="br2 bg-near-white tc flex items-center justify-center flex-column pa4"
        data-test-component="AnimalsTableNoData"
        data-test-element="filtered-out-container"
        data-testid="animals-filtered-out-container"
      >
        <div className="mw6 center ph4 pa4 mv4">
          <h3 className="mb2" data-testid="title">
            No animals match the current query
          </h3>
          <p className="mb4" data-testid="description">
            This study contains animals, but none match your active filters or search.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="br2 bg-near-white tc flex items-center justify-center flex-column pa4"
      data-test-component="AnimalsTableNoData"
      data-test-element="container"
      data-testid="create-animals-container"
    >
      <div className="mw6 center ph4 pa4 mv4">
        <h3 className="mb2" data-testid="title">
          {atunesIntegrationEnabled ? 'Import Animals' : 'Create animals'}
        </h3>
        <p className="mb3" data-testid="description">
          {atunesIntegrationEnabled
            ? 'To import animals and cages from tick@lab go to '
            : 'To import cages from colony, go to '}
          <Link className="link" to={`/studies/${studyId}/cages/`}>
            cages
          </Link>
          .
        </p>
        {!atunesIntegrationEnabled && (
          <div data-test-element="actions">
            {_isNotEmpty(actions) ? (
              <SelectDropDown
                title="Create animals"
                alignMenu="right"
                className="pr4"
                testId="create-animals-select-dropdown"
              >
                <ActionList actions={actions} />
              </SelectDropDown>
            ) : (
              <Button onClick={onCreateNewAnimals} className="pr4" testId="create-animals-button">
                Create new animals
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimalsTableNoData;
